import { createRouter, createWebHistory } from 'vue-router';
import MainLayout from '../layouts/MainLayout.vue';
import Bots from '../views/Bots.vue';
import NotFound from '../views/NotFound.vue';
import BotEditor from '../views/BotEditor.vue';
import UnifiedFlowEditor from '../views/UnifiedFlowEditor.vue';
import Billing from '../views/Billing.vue';
import WorkspaceSettings from '../views/WorkspaceSettings.vue';
import Invites from '../views/Invites.vue';
import api from '../services/api';
import { waitForClerk, getClerkAuth } from '../utils/clerkReady';

import QA from '../views/bot-editor/QA.vue';
import Flows from '../views/bot-editor/Flows.vue';
import Tools from '../views/bot-editor/Tools.vue';
import Connectors from '../views/bot-editor/Connectors.vue';
import Prompting from '../views/bot-editor/Prompting.vue';
import Settings from '../views/bot-editor/Settings.vue';
import Analytics from '../views/bot-editor/Analytics.vue';
import Transcripts from '../views/bot-editor/Transcripts.vue';
import Deploy from '../views/bot-editor/Deploy.vue';
import BotTestSnapshot from '../views/bot-editor/BotTestSnapshot.vue';

const StorageView = () => import('../views/bot-editor/StorageView.vue');
const StorageDetailView = () => import('../views/bot-editor/StorageDetailView.vue');
const SignInView = () => import('../views/auth/SignInView.vue');
const SignUpView = () => import('../views/auth/SignUpView.vue');
const OAuthConsentView = () => import('../views/auth/OAuthConsentView.vue');

// Path interno (route SPA) verso cui mandare gli utenti non autenticati.
const SIGN_IN_PATH = '/sign-in';

const routes = [
  { path: '/', redirect: '/app/bots' },
  { path: '/login', redirect: '/sign-in' },
  // Catch-all opzionale: i componenti Clerk usano sotto-path (es. /sign-up/verify-email-address,
  // /sign-in/factor-one, /sign-in/sso-callback) per gli step del flusso. Senza questo, la
  // verifica del codice non si monta.
  { path: '/sign-in/:catchAll(.*)*', name: 'SignIn', component: SignInView },
  { path: '/sign-up/:catchAll(.*)*', name: 'SignUp', component: SignUpView },
  // Pagina di consenso OAuth (client MCP registrati dinamicamente via Clerk).
  // Pubblica: ci si arriva già autenticati dopo il login Clerk; la guard non la
  // tratta come pagina di auth, quindi nessun redirect indesiderato.
  { path: '/oauth/consent', name: 'OAuthConsent', component: OAuthConsentView },
  {
    path: '/app',
    component: MainLayout,
    meta: { requiresAuth: true },
    redirect: '/app/bots',
    children: [
      // Legacy redirects — resolved by guard to /app/workspace/:id/bots
      { path: 'bots', name: 'BotsLegacy', redirect: { name: 'WorkspaceRedirect' } },
      { path: 'billing', name: 'BillingLegacy', redirect: { name: 'WorkspaceRedirect' } },

      // Global invites page (not workspace-scoped)
      { path: 'invites', name: 'Invites', component: Invites },

      // Workspace-scoped routes
      {
        path: 'workspace/:workspaceId',
        meta: { requiresAuth: true },
        children: [
          { path: 'bots', name: 'Bots', component: Bots },
          {
            path: 'billing',
            name: 'Billing',
            component: Billing,
            beforeEnter: async (to, from, next) => {
              // Hot path: use the in-memory tokenStore if already hydrated
              const { useTokenStore } = await import('../composables/useTokenStore');
              const { store: tokenStore } = useTokenStore();
              if (tokenStore.tokenAllowance === -1 || tokenStore.managed) {
                return next({ name: 'Bots', params: { workspaceId: to.params.workspaceId } });
              }
              // Cold-load / direct URL: store may not yet be hydrated. Defensive fetch.
              try {
                const { data } = await api.get(`/workspaces/${to.params.workspaceId}/billing/tokens`);
                if (data?.data?.tokenAllowance === -1 || data?.data?.managed) {
                  return next({ name: 'Bots', params: { workspaceId: to.params.workspaceId } });
                }
              } catch (err) {
                Console.warn('[router] Billing guard fetch failed', err);
              }
              next();
            }
          },
          { path: 'settings', name: 'WorkspaceSettings', component: WorkspaceSettings },
          { path: 'members', name: 'WorkspaceMembersLegacy', redirect: to => `/app/workspace/${to.params.workspaceId}/settings` },
          {
            path: 'bots/:id',
            name: 'BotEditor',
            component: BotEditor,
            redirect: to => `/app/workspace/${to.params.workspaceId}/bots/${to.params.id}/storage`,
            children: [
              { path: 'prompting', name: 'BotPrompting', component: Prompting },
              { path: 'storage', name: 'BotStorage', component: StorageView },
              { path: 'storage/:storageId', name: 'BotStorageDetail', component: StorageDetailView },
              { path: 'qa', name: 'BotQA', component: QA },
              { path: 'flows', name: 'BotFlows', component: Flows },
              { path: 'tools', name: 'BotTools', component: Tools },
              { path: 'webhooks', name: 'BotWebhooks', component: () => import('../views/bot-editor/Webhooks.vue') },
              { path: 'connectors', name: 'BotConnectors', component: Connectors },
              { path: 'analytics', name: 'BotAnalytics', component: Analytics },
              { path: 'transcripts', name: 'BotTranscripts', component: Transcripts },
              { path: 'settings', name: 'BotSettings', component: Settings },
              { path: 'deploy', name: 'BotDeploy', component: Deploy },
              { path: 'test-snapshot', name: 'BotTestSnapshot', component: BotTestSnapshot },
              { path: 'customize-chat', name: 'BotCustomizeChat', component: () => import('../views/bot-editor/CustomizeChatroom.vue') },
              { path: 'whatsapp-templates', name: 'BotWhatsappTemplates', component: () => import('../views/bot-editor/WhatsappTemplates.vue') },
            ],
          },
          {
            path: 'bots/:id/flows/editor/:flowId',
            name: 'FlowEditor',
            component: UnifiedFlowEditor,
            meta: { fullHeight: true },
          },
          {
            path: 'bots/:id/tools/editor/:toolFlowId',
            name: 'ToolFlowEditor',
            component: UnifiedFlowEditor,
            meta: { fullHeight: true },
          },
          {
            path: 'bots/:id/webhooks/editor/:webhookFlowId',
            name: 'WebhookFlowEditor',
            component: UnifiedFlowEditor,
            meta: { fullHeight: true },
          },
        ],
      },

      // Named redirect target used by legacy paths
      {
        path: 'workspace-redirect',
        name: 'WorkspaceRedirect',
        redirect: () => ({ name: 'Bots', params: { workspaceId: 'resolve' } }),
      },

      { path: 'admin/:pathMatch(.*)*', redirect: to => `/admin/${to.params.pathMatch || ''}` },
    ],
  },
  {
    path: '/admin',
    component: () => import('../layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
    redirect: { name: 'AdminUsers' },
    children: [
      { path: 'users',          name: 'AdminUsers',          component: () => import('../views/admin/AdminUsers.vue'),         meta: { titleKey: 'admin.layout.sidebar.users' } },
      { path: 'users/:id',      name: 'AdminUserDetail',     component: () => import('../views/admin/AdminUserDetail.vue'),    meta: { titleKey: 'admin.layout.sidebar.users' } },
      { path: 'workspaces',     name: 'AdminWorkspaces',     component: () => import('../views/admin/AdminWorkspaces.vue'),    meta: { titleKey: 'admin.layout.sidebar.workspaces' } },
      { path: 'bots',           name: 'AdminBots',           component: () => import('../views/admin/AdminBots.vue'),          meta: { titleKey: 'admin.layout.sidebar.bots' } },
      { path: 'models',         name: 'AdminModels',         component: () => import('../views/admin/AdminModels.vue'),        meta: { titleKey: 'admin.layout.sidebar.models' } },
      { path: 'providers',      name: 'AdminProviders',      component: () => import('../views/admin/AdminProviders.vue'),     meta: { titleKey: 'admin.layout.sidebar.providers' } },
      { path: 'vapi',           name: 'AdminVapi',           component: () => import('../views/admin/AdminVapi.vue'),          meta: { titleKey: 'admin.vapi.title' } },
      { path: 'gptlive',        name: 'AdminGptLive',        component: () => import('../views/admin/AdminGptLive.vue'),       meta: { titleKey: 'admin.gptlive.title' } },
      { path: 'livechat',       name: 'AdminLiveChat',       component: () => import('../views/admin/AdminLiveChat.vue'),      meta: { titleKey: 'admin.livechat.title' } },
      { path: 'prompting',      name: 'AdminPrompting',      component: () => import('../views/admin/AdminPrompting.vue'),     meta: { titleKey: 'admin.layout.sidebar.prompting' } },
      { path: 'tool-limits',    name: 'AdminToolLimits',     component: () => import('../views/admin/AdminToolLimits.vue'),    meta: { titleKey: 'admin.layout.sidebar.toolLimits' } },
      { path: 'mailer',         name: 'AdminMailer',         component: () => import('../views/admin/AdminMailer.vue'),        meta: { titleKey: 'admin.layout.sidebar.mailer' } },
      { path: 'notifications',  name: 'AdminNotifications',  component: () => import('../views/admin/AdminNotifications.vue'), meta: { titleKey: 'admin.layout.sidebar.notifications' } },
      { path: 'data-browser',   name: 'AdminDataBrowser',    component: () => import('../views/admin/AdminDataBrowser.vue'),   meta: { titleKey: 'admin.layout.sidebar.dataBrowser' } },
      { path: 'whatsapp',       name: 'AdminWhatsapp',       component: () => import('../views/admin/AdminWhatsapp.vue'),       meta: { titleKey: 'admin.layout.sidebar.whatsapp' } },
      { path: 'address',       name: 'AdminAddress',        component: () => import('../views/admin/AdminAddress.vue'),        meta: { titleKey: 'admin.layout.sidebar.address' } },
      { path: 'web-search',   name: 'AdminWebSearch',      component: () => import('../views/admin/AdminWebSearch.vue'),     meta: { titleKey: 'admin.layout.sidebar.webSearch' } },
    ],
  },
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: NotFound },
];

const router = createRouter({ history: createWebHistory(), routes });

async function resolveWorkspaceId() {
  try {
    const { useWorkspaceStore } = await import('../stores/workspaceStore');
    const store = useWorkspaceStore();
    if (!store.workspaces.length) await store.loadWorkspaces();

    const lastId = store.getLastWorkspaceId();
    const resolved = store.resolveWorkspace(lastId);
    if (resolved) {
      store.switchWorkspace(resolved);
      return resolved._id;
    }
  } catch (err) {
    Console.error('[router] resolveWorkspaceId failed:', err);
  }
  return null;
}

router.beforeEach(async (to, from, next) => {
  await waitForClerk();
  const auth = getClerkAuth();

  const isAuthPage = to.path.startsWith('/sign-in') || to.path.startsWith('/sign-up');

  // Utente già loggato che apre una pagina di auth → mandalo nell'app.
  // Eccezione: se Clerk ha aggiunto un `redirect_url` interno (es. il flusso
  // OAuth/MCP manda a /sign-in?redirect_url=/oauth/consent?...), onoralo invece
  // di sbattere su /app/bots — altrimenti la schermata di consenso non viene
  // mai raggiunta. Solo destinazioni same-origin; l'esterno resta a Clerk.
  if (isAuthPage && auth.isSignedIn) {
    const redirectUrl = Array.isArray(to.query.redirect_url) ? to.query.redirect_url[0] : to.query.redirect_url;
    if (typeof redirectUrl === 'string' && redirectUrl) {
      try {
        const u = new URL(redirectUrl, window.location.origin);
        if (u.origin === window.location.origin && u.pathname !== to.path) {
          return next(u.pathname + u.search + u.hash);
        }
      } catch { /* redirect_url malformato → fallback sotto */ }
    }
    return next('/app/bots');
  }

  // Route pubbliche (incluse /sign-in e /sign-up) passano senza auth.
  if (!to.matched.some(r => r.meta.requiresAuth)) return next();

  // Route protette senza sessione → pagina di login interna.
  if (!auth.isSignedIn) {
    return next(SIGN_IN_PATH);
  }

  if (to.matched.some(r => r.meta.requiresAdmin)) {
    if (auth.user?.publicMetadata?.role !== 'admin') {
      const workspaceId = await resolveWorkspaceId();
      return workspaceId
        ? next({ name: 'Bots', params: { workspaceId } })
        : next({ name: 'Invites' });
    }
  }

  // Redirect legacy routes and root /app to workspace-scoped bots
  if (to.name === 'BotsLegacy' || to.name === 'BillingLegacy' || to.name === 'WorkspaceRedirect' || to.path === '/app' || to.path === '/app/bots' || to.path === '/') {
    const workspaceId = await resolveWorkspaceId();
    if (!workspaceId) return next({ name: 'Invites' });
    const targetName = to.name === 'BillingLegacy' ? 'Billing' : 'Bots';
    return next({ name: targetName, params: { workspaceId } });
  }

  // Static redirects (e.g. WorkspaceRedirect → Bots{workspaceId:'resolve'}) bypass the
  // guard because Vue Router resolves all redirects before firing beforeEach. Detect any
  // workspace-scoped route where workspaceId is not a valid ObjectId and re-resolve it.
  if (to.params.workspaceId && !/^[0-9a-f]{24}$/i.test(String(to.params.workspaceId))) {
    const workspaceId = await resolveWorkspaceId();
    if (!workspaceId) return next({ name: 'Invites' });
    return next({ name: to.name, params: { ...to.params, workspaceId } });
  }

  next();
});

export default router;
